import React from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type {
  SymbolAnalysisPayload,
  SymbolAnalysisRuntime,
} from '../../core/types/payloads/symbol_analysis.types';
import { SymbolAnalysisStaff } from '../../core/music/vex/SymbolAnalysisStaff';

function dispatchSubmit(dispatch: ActivityViewProps['dispatch'], action: string, data?: unknown) {
  void dispatch({ type: action, payload: data });
}

function staffInstruction(payload: SymbolAnalysisPayload): string {
  if (payload.phraseBarNumbers && payload.phraseBarNumbers.length > 1) {
    return `Observa los compases ${payload.phraseBarNumbers.join(' y ')} del repertorio:`;
  }
  return `Observa con atención el compás #${payload.targetBarNumber} del repertorio:`;
}

export function SymbolAnalysisStudent({
  payload,
  runtime,
  dispatch,
  participantId,
}: ActivityViewProps<'symbol_analysis'>) {
  const rt = runtime as SymbolAnalysisRuntime;
  const chosen = participantId ? rt.responses[participantId] ?? '' : '';
  const finalized = participantId ? rt.finalized[participantId] : false;
  const correctId = payload.options.find((o) => o.isCorrect)?.id;

  return (
    <div className="eval-block eval-block--symbol-analysis">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__body text-sm text-[var(--sala-ink-muted)]">{staffInstruction(payload)}</p>

      <div className="symbol-analysis-staff">
        <SymbolAnalysisStaff
          patternTokens={payload.patternTokens}
          timeSignature={payload.timeSignature}
          targetBarNumber={payload.targetBarNumber}
          modifiers={payload.modifiers}
          scoreEvents={payload.scoreEvents}
          scoreEventsByBar={payload.scoreEventsByBar}
          phraseBarNumbers={payload.phraseBarNumbers}
          barTokenStarts={payload.barTokenStarts}
          highlight={payload.highlightedElement}
          readingMethod={payload.rhythmMethod}
          showLabels={payload.graphicMode === 'symbols-labels'}
        />
      </div>

      <p className="symbol-analysis-question mt-4">{payload.questionText}</p>

      <ul className="symbol-analysis-options">
        {payload.options.map((opt) => {
          const isChosen = chosen === opt.id;
          const showResult = finalized && isChosen;
          const isCorrect = opt.isCorrect;
          return (
            <li key={opt.id}>
              <button
                type="button"
                className={`symbol-analysis-option${isChosen ? ' symbol-analysis-option--active' : ''}${
                  showResult
                    ? isCorrect
                      ? ' symbol-analysis-option--correct'
                      : ' symbol-analysis-option--wrong'
                    : ''
                }`}
                disabled={finalized}
                onClick={() => dispatchSubmit(dispatch, 'FILL', { value: opt.id })}
              >
                <span className="symbol-analysis-option__marker" aria-hidden>
                  {isChosen ? '●' : '○'}
                </span>
                <span>{opt.text}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {finalized && correctId && chosen !== correctId && (
        <p className="symbol-analysis-feedback mt-3 text-sm text-emerald-400">
          Respuesta correcta: {payload.options.find((o) => o.id === correctId)?.text}
        </p>
      )}

      {!finalized && (
        <button
          type="button"
          className="sala-btn-hero mt-4 text-xs"
          disabled={!chosen}
          onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}
        >
          Enviar
        </button>
      )}
    </div>
  );
}

export function SymbolAnalysisHost({ payload, runtime }: ActivityViewProps<'symbol_analysis'>) {
  const rt = runtime as SymbolAnalysisRuntime | undefined;
  const submitted = rt?.aggregates?.submitted ?? 0;
  const correct = payload.options.find((o) => o.isCorrect);

  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <SymbolAnalysisStaff
        patternTokens={payload.patternTokens}
        timeSignature={payload.timeSignature}
        targetBarNumber={payload.targetBarNumber}
        modifiers={payload.modifiers}
        scoreEvents={payload.scoreEvents}
        scoreEventsByBar={payload.scoreEventsByBar}
        phraseBarNumbers={payload.phraseBarNumbers}
        barTokenStarts={payload.barTokenStarts}
        highlight={payload.highlightedElement}
      />
      <p className="eval-block__hint mt-2">{payload.questionText}</p>
      {correct && <p className="eval-block__hint">Respuesta: {correct.text}</p>}
      {submitted > 0 && <p className="eval-block__hint">Enviados: {submitted}</p>}
    </div>
  );
}

export function SymbolAnalysisProjector(props: ActivityViewProps<'symbol_analysis'>) {
  return <SymbolAnalysisHost {...props} />;
}
